import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {Box, Group, Text, createStyles, keyframes} from '@mantine/core';
import React from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {SIGNUP_STEPS} from './constants';

type StepStatus = 'completed' | 'current' | 'upcoming';

const INDICATOR_SIZE_PX = 20;
const STEP_TRANSITION_MS = 280;

const indicatorEnter = keyframes({
  '0%': {transform: 'scale(0.92)', opacity: 0.85},
  '60%': {transform: 'scale(1.05)'},
  '100%': {transform: 'scale(1)', opacity: 1},
});

const checkEnter = keyframes({
  '0%': {opacity: 0, transform: 'scale(0.6)'},
  '100%': {opacity: 1, transform: 'scale(1)'},
});

interface SignupProgressListProps {
  activeStepIndex: number;
  allStepsComplete?: boolean;
}

function getStepStatus(stepIndex: number, activeStepIndex: number, allStepsComplete: boolean): StepStatus {
  if (allStepsComplete || stepIndex < activeStepIndex) {
    return 'completed';
  }
  if (stepIndex === activeStepIndex) {
    return 'current';
  }
  return 'upcoming';
}

export default function SignupProgressList({
  activeStepIndex,
  allStepsComplete = false,
}: SignupProgressListProps): JSX.Element {
  const {classes, cx} = useStyles();

  return (
    <Box
      aria-label="Signup progress"
      className={classes.list}
      component="ol"
      {...getTestProps({component: 'SignupProgressList', element: 'root'})}
    >
      {SIGNUP_STEPS.map((stepLabel, index) => {
        const status = getStepStatus(index, activeStepIndex, allStepsComplete);
        const isLast = index === SIGNUP_STEPS.length - 1;
        const connectorCompleted = status === 'completed';

        return (
          <Box
            key={stepLabel}
            aria-current={status === 'current' ? 'step' : undefined}
            component="li"
            {...getTestProps({component: 'SignupProgressList', element: `step-${index}`})}
          >
            <Group align="center" className={classes.stepRow} noWrap={true} spacing="sm">
              <Box
                aria-hidden={true}
                className={cx(classes.indicator, {
                  [classes.indicatorCompleted]: status === 'completed',
                  [classes.indicatorCurrent]: status === 'current',
                  [classes.indicatorUpcoming]: status === 'upcoming',
                })}
                {...getTestProps({component: 'SignupProgressList', element: `step-${index}-indicator`})}
              >
                {status === 'completed' && (
                  <BenchlingIcon aria-hidden={true} className={classes.checkIcon} icon="check" />
                )}
              </Box>
              <Text
                className={cx(classes.label, {
                  [classes.labelCurrent]: status === 'current',
                  [classes.labelUpcoming]: status === 'upcoming',
                })}
              >
                {stepLabel}
                {status !== 'upcoming' && (
                  <span className={classes.visuallyHidden}>
                    {status === 'current' ? ', current step' : ', completed'}
                  </span>
                )}
              </Text>
            </Group>
            {!isLast && (
              <Box
                aria-hidden={true}
                className={cx(classes.connector, {
                  [classes.connectorCompleted]: connectorCompleted,
                  [classes.connectorUpcoming]: !connectorCompleted,
                })}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

const useStyles = createStyles((theme) => ({
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  stepRow: {
    minHeight: INDICATOR_SIZE_PX,
  },
  indicator: {
    alignItems: 'center',
    borderRadius: '50%',
    display: 'flex',
    flex: `0 0 ${INDICATOR_SIZE_PX}px`,
    height: INDICATOR_SIZE_PX,
    justifyContent: 'center',
    transition: `background-color ${STEP_TRANSITION_MS}ms ease, border-color ${STEP_TRANSITION_MS}ms ease, box-shadow ${STEP_TRANSITION_MS}ms ease, transform ${STEP_TRANSITION_MS}ms ease`,
    width: INDICATOR_SIZE_PX,
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  indicatorUpcoming: {
    backgroundColor: 'transparent',
    border: `2px solid ${theme.fn.rgba(theme.white, 0.5)}`,
  },
  indicatorCurrent: {
    animation: `${indicatorEnter} 0.45s ease`,
    backgroundColor: theme.white,
    border: `2px solid ${theme.white}`,
    boxShadow: `0 0 0 4px ${theme.fn.rgba(theme.white, 0.18)}`,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  indicatorCompleted: {
    backgroundColor: theme.white,
    border: `2px solid ${theme.white}`,
  },
  checkIcon: {
    animation: `${checkEnter} 0.3s ease`,
    color: Color.GRAPE_400,
    fontSize: 10,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  label: {
    color: theme.white,
    flex: 1,
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
    lineHeight: `${INDICATOR_SIZE_PX}px`,
    minWidth: 0,
    transition: `color ${STEP_TRANSITION_MS}ms ease, font-weight ${STEP_TRANSITION_MS}ms ease, opacity ${STEP_TRANSITION_MS}ms ease`,
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  labelCurrent: {
    fontWeight: 700,
  },
  labelUpcoming: {
    fontWeight: 500,
  },
  visuallyHidden: {
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1,
  },
  connector: {
    height: 24,
    marginLeft: INDICATOR_SIZE_PX / 2 - 1,
    transition: `background-color ${STEP_TRANSITION_MS}ms ease, opacity ${STEP_TRANSITION_MS}ms ease`,
    width: 2,
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  connectorCompleted: {
    backgroundColor: theme.fn.rgba(theme.white, 0.85),
  },
  connectorUpcoming: {
    backgroundColor: theme.fn.rgba(theme.white, 0.28),
  },
}));
